<script setup>
import {ref, onMounted, computed, onUnmounted, watch} from "vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {gregorianDate} from "@/utils/gregorianDate.js";
import StateColumn from "@/views/orders/Shared/StateColumn.vue";
import {formatPrice} from "@/utils/priceFormatter.js";
import Filters from "@/views/orders/Filters/index.vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {Icon} from "@iconify/vue";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import {showMessage} from "@/utils/showMessage.js";
import {useOrderFilter} from "@/composables/useOrderFilter.js";
import {useFilterByState} from "@/composables/useFilterByShopState.js";
import {useRouter} from "vue-router";
import SortIcon from "@/components/datatable/sortIcon.vue";

const router = useRouter();
let interval = null;
const cleared = ref(false);
const search = computed(() => ordersStore.currencySellOrdersSearch);
const ordersStore = useMainOrdersStore();
const orders = computed(() => {
  return ordersStore.getOrders('currencySell').map(order => ({
    ...order,
    price: order.price || order.price === 0 ? Number(order.price) : null,
    takersCut: order.takersCut || order.takersCut === 0 ? Number(order.takersCut) : null,
    revenue: order.revenue || order.revenue === 0 ? Number(order.revenue) : null,
  }))
});

const fetchNewOrders = async () => {
  if (cleared.value) return;

  if (orders.value.length > 0 && !ordersStore.isGettingNewOrders) {
    await ordersStore.action_getOrders('Currency');
  }

  if (!cleared.value) {
    interval = setTimeout(fetchNewOrders, 2000);
  }
};

async function applyFilters() {
  ordersStore.reset_lastUpdate();
  ordersStore.reset_orders('currencySellOrders');
  await ordersStore.action_getOrders('Currency');
}

function orderDetails({data}) {
  const route = {name: 'currency-sell-order-details', params: {id: 0}}
  if (data.uuid) {
    route.params.id = data.uuid
  } else {
    route.params.id = data.id
  }
  router.push(route)
}

function onSortChange(params) {
  const sortOrder = params.sortOrder === 1 ? 'asc' : 'desc';
  ordersStore.setCurrencySellOrdersSort(params.sortField, sortOrder);
}

const {filtered} = useOrderFilter(
    orders,
    search,
    'currencySell'
);

const {filteredByState: filteredOrdersByShopState} = useFilterByState(
    filtered,
    computed(() => ordersStore.shopStateFilter), // حتما value اضافه نشه، کامپیوتر خودش reactiveه
    "shopState"
);

function copyUUIDs() {
  const selectedOrders = ordersStore.selectedCurrencySellOrders.length > 0 ? ordersStore.selectedCurrencySellOrders : filteredOrdersByShopState.value

  if (!selectedOrders.length) {
    alert("No UUIDs to copy!");
    return;
  }
  const uuids = selectedOrders
      .map(order => order.uuid)
      .filter(Boolean)
      .join('\n');

  navigator.clipboard.writeText(uuids)
      .then(() => {
        showMessage(selectedOrders.length === 1 ? `${selectedOrders.length} UUID copied` : `${selectedOrders.length} UUIDs copied`, 'success');
      })
      .catch(err => {
        console.error("Failed to copy UUIDs:", err);
        showMessage('Failed to copy UUIDs', 'error');
      });
}

watch(filteredOrdersByShopState, (newVal) => {
  ordersStore.currencySellFilteredOrders = newVal;
});

onMounted(() => {
  ordersStore.action_getOrders('Currency');
  fetchNewOrders();
})

onUnmounted(() => {
  clearTimeout(interval);
  cleared.value = true;
});
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-row justify-between gap-4">
        <Breadcrumbs/>
        <AllButtons summaryPermission="ORDERS_CURRENCY_ACCESS_SUMMARY" addOrderPermission="ORDERS_CURRENCY_UPDATE"
                    addOrderRoute="currency-sell-order-add" summary="CurrencySellSummary" :applyFilter="applyFilters"
                    v-model="ordersStore.currencySellOrdersSearch" @copyUUIDs="copyUUIDs"/>
      </div>
      <div v-if="ordersStore.isLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <DataTable v-else
                 :nullSortOrder="-1"
                 :value="filteredOrdersByShopState"
                 scrollable
                 scrollHeight="flex"
                 :virtualScrollerOptions="{ itemSize: 32 }"
                 resizableColumns
                 removableSort
                 stripedRows
                 class="text-xs w-full"
                 size="small"
                 tableStyle="width: 100%; table-layout: fixed;"
                 :sortField="ordersStore.currencySellOrdersSort?.field"
                 :sortOrder="ordersStore.currencySellOrdersSort?.type === 'asc' ? 1 : -1"
                 row-hover
                 @row-click="orderDetails"
                 @sort="onSortChange"
                 dataKey="id"
                 v-model:selection="ordersStore.selectedCurrencySellOrders"
                 :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
      >
        <Column selectionMode="multiple" style="width: 36px;" headerStyle="width: 36px !important"></Column>
        <Column field="uuid" header="UUID" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span @click.stop class="w-full block h-full py-[0.375rem]">{{ slotProps.data.uuid }}</span>
          </template>
        </Column>
        <Column field="store" header="Store" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="game" header="Game" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="g2gSellID" header="Sell ID" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap"
                style="width: 12%; min-width: 120px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
                  <span @click.stop class="w-full block h-full overflow-hidden text-ellipsis whitespace-nowrap">
                    {{ slotProps.data.g2gSellID }}
                  </span>
          </template>
        </Column>
        <Column field="title" header="Title" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 15%; min-width: 200px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="customerName" header="Customer" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.customerName
                  ? slotProps.data.customerName
                  : slotProps.data.customer ? slotProps.data.customer : ''
            }}
          </template>
        </Column>
        <Column field="takers" header="Takers" :sortable="true"
                class="cursor-pointer"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
                  <span @click.stop class="w-full block h-full overflow-hidden text-ellipsis whitespace-nowrap">
                    {{ slotProps.data.takers }}
                  </span>
          </template>
        </Column>
        <Column field="price" header="Price" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{ slotProps.data.price || slotProps.data.price === 0 ? formatPrice(slotProps.data.price) + ' $' : '' }}
          </template>
        </Column>
        <Column field="takersCut" header="Cut" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap text-red-400 cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.takersCut || slotProps.data.takersCut === 0 ? formatPrice(slotProps.data.takersCut) + ' $' : ''
            }}
          </template>
        </Column>
        <Column field="revenue" header="Revenue" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
                  <span
                      :class="{ '!text-red-400': slotProps.data.revenue < 0, '!text-success-500': slotProps.data.revenue > 0 }">
                    {{
                      slotProps.data.revenue || slotProps.data.revenue === 0 ? formatPrice(slotProps.data.revenue) + ' $' : ''
                    }}
                  </span>
          </template>
        </Column>
        <Column field="state" header="Status" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <StateColumn :state="slotProps.data.state"/>
          </template>
        </Column>
        <Column field="shopState" header="Shop State" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
                  <span class="block w-full max-w-max truncate">
                    {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
                  </span>
          </template>
        </Column>
        <Column field="creationTime" header="Creation Time" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
                  <span class="block w-full max-w-max truncate">
                    {{ gregorianDate(slotProps.data.creationTime, 'DD MMM YYYY') }}
                  </span>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">No Data</h1>
        </template>
      </DataTable>
    </div>
    <Filters v-if="ordersStore.isShowFilterModal" @applyFilter="applyFilters"
               :filters="['stores', 'games', 'states', 'shopStates']" service="Currency"/>
  </div>
</template>